/**
 * CTAFrame — Forensic End Frame
 * ---------------------------------------------------------------
 * Quiet, archival, brand-locked. No social CTAs. No "follow for
 * more". A held line, a small mark, then silence.
 */

import { AbsoluteFill, useCurrentFrame, interpolate } from "remotion";
import { PALETTE, END_FRAMES } from "../utils/timing";

type EndKey = keyof typeof END_FRAMES;

interface Props {
  /** Pre-set forensic ending — defaults to "practice". */
  preset?: EndKey;
  headline?: string;
  subline?: string;
  micro?: string;
}

export const CTAFrame: React.FC<Props> = ({
  preset = "practice",
  headline,
  subline,
  micro,
}) => {
  const p = END_FRAMES[preset];
  const finalHeadline = headline ?? p.headline;
  const finalSubline = subline ?? p.subline;
  const finalMicro = micro ?? p.micro;

  const frame = useCurrentFrame();
  // Slow restrained fade — no spring overshoot
  const enter = interpolate(frame, [0, 36], [0, 1], { extrapolateRight: "clamp" });
  const lift = interpolate(frame, [0, 42], [10, 0], { extrapolateRight: "clamp" });
  const lineWidth = interpolate(frame, [6, 48], [0, 220], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: PALETTE.bgDeep,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 96,
        opacity: enter,
      }}
    >
      {/* archival corner ticks */}
      {[
        { top: 56, left: 56 },
        { top: 56, right: 56 },
        { bottom: 56, left: 56 },
        { bottom: 56, right: 56 },
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            ...pos,
            width: 22,
            height: 22,
            borderColor: PALETTE.cyanSoft,
            borderStyle: "solid",
            borderWidth: 0,
            borderTopWidth: pos.top ? 1 : 0,
            borderBottomWidth: pos.bottom ? 1 : 0,
            borderLeftWidth: pos.left ? 1 : 0,
            borderRightWidth: pos.right ? 1 : 0,
            opacity: 0.5,
          }}
        />
      ))}

      <div
        style={{
          width: lineWidth,
          height: 1,
          backgroundColor: PALETTE.cyanSoft,
          marginBottom: 56,
        }}
      />
      <div
        style={{
          color: PALETTE.silver,
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: 52,
          fontWeight: 400,
          lineHeight: 1.28,
          letterSpacing: "-0.005em",
          textAlign: "center",
          maxWidth: 880,
          transform: `translateY(${lift}px)`,
        }}
      >
        {finalHeadline}
      </div>
      <div
        style={{
          marginTop: 56,
          color: PALETTE.cyanSoft,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 22,
          letterSpacing: "0.4em",
          textTransform: "uppercase",
        }}
      >
        {finalSubline}
      </div>
      <div
        style={{
          marginTop: 22,
          color: PALETTE.textDim,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 14,
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          opacity: 0.7,
        }}
      >
        {finalMicro}
      </div>
    </AbsoluteFill>
  );
};
